import { useNavigate } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { isAxiosError } from "axios";
import { logout } from "@/api/auth";
import { useSessionMutate } from "@/security/RouteGuards";
import { deleteToken } from "@/security/authentication";
import { captureException } from "@/observability/sentry";
import { useFederatedUser } from "@/security/federation";
import webshareAPI from "../api";
import { LocalStorageKeysThatShouldNotBePersist } from "@/config/localStorageKeys";
import { removeFromStorage } from "@/utils/localStorageUtils";
import useIntercom from "./useIntercom";
import { resetKlaviyo } from "@/tracking/klaviyo";
import { clearTrackingId } from "@/tracking/trackingIds";
import { useSWRConfig } from "swr";

/**
 * Logout API failures with no user impact: the session is already dead (401),
 * the account is suspended (403), or the client is offline — local cleanup
 * proceeds regardless. PROXYCONTROLPANELDASHBOARD-7EF.
 */
const isExpectedLogoutFailure = (err: unknown) =>
  isAxiosError(err) &&
  (["ECONNABORTED", "ERR_NETWORK"].includes(err.code || "") ||
    err.response?.status === 401 ||
    err.response?.status === 403);

/**
 * Simple wrapper around try/catch, so it's not so cumbersome and repetitive.
 */
const executeAndCapture = async (
  action: () => unknown,
  errorMessage: string,
  isExpected?: (err: unknown) => boolean
) => {
  try {
    await Promise.resolve(action());
  } catch (err) {
    if (isExpected?.(err)) {
      return;
    }
    const ourError = new Error(errorMessage, { cause: err });
    captureException(ourError, {
      level: "warning",
      extra: {
        causeMessage: err instanceof Error ? err.message : String(err),
        causeStack: err instanceof Error ? err.stack : undefined,
      },
    });
  }
};

interface IProps {
  shouldRedirectToLogin?: boolean;
}

export default function useAccountLogout(props?: IProps) {
  const shouldRedirect =
    typeof props?.shouldRedirectToLogin === "undefined" ? true : false;

  const { cache } = useSWRConfig();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { deleteFederationToken, federationToken } = useFederatedUser();
  const { setSessionToken } = useSessionMutate();
  const { shutdown } = useIntercom();

  return async function () {
    await executeAndCapture(
      () => logout(),
      "logout api error",
      isExpectedLogoutFailure
    );

    const cleanupTasks = [
      executeAndCapture(
        () => posthog?.reset(true),
        "posthog reset on logout error"
      ),
      executeAndCapture(
        () => clearTrackingId(),
        "clear tracking id on logout error"
      ),
      // Clears Intercom's session so whoever comes next starts their own; the
      // widget itself stays, and `IntercomProviderInternal` boots it again
      // anonymously once the session is gone (R-1873).
      executeAndCapture(() => shutdown(), "intercom shutdown on logout error"),
      executeAndCapture(() => resetKlaviyo(), "klaviyo reset on logout error"),
      executeAndCapture(() => deleteToken(), "deleteToken on logout error"),
      executeAndCapture(
        () => setSessionToken?.(undefined),
        "setSessionToken on logout error"
      ),
      executeAndCapture(
        () => webshareAPI.removeAuthToken(),
        "removeAuthToken on logout error"
      ),
      // Otherwise whoever logs in next acts as the sub-user this session viewed.
      executeAndCapture(
        () => webshareAPI.removeSubUserHeader(),
        "removeSubUserHeader on logout error"
      ),
      executeAndCapture(
        () => federationToken && deleteFederationToken(),
        "deleteFederationToken on logout error"
      ),
      executeAndCapture(() => {
        LocalStorageKeysThatShouldNotBePersist.forEach((key) => {
          removeFromStorage(key);
        });
      }, "clearing local storage on logout error"),
      executeAndCapture(() => {
        for (const key of cache.keys()) {
          cache.delete(key);
        }
      }, "SWR cache clear on logout error"),
    ];

    await Promise.all(cleanupTasks);

    if (shouldRedirect) {
      await executeAndCapture(
        () => navigate({ to: "/login" }),
        "redirect on logout error"
      );
    }
  };
}
